import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContentItemRow {
  id: string;
  type: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: Record<string, any>;
  seo: Record<string, any>;
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

/**
 * Fetch a single content item by slug.
 * Respects status: only "published" is public.
 * Preview mode fetches any status for authenticated admins.
 */
export const useContentItem = (
  slug: string | undefined,
  previewMode: boolean = false
) => {
  return useQuery({
    queryKey: ["content-item", slug, previewMode],
    queryFn: async () => {
      if (!slug) return null;

      let query = supabase
        .from("content_items")
        .select("*")
        .eq("slug", slug);

      if (!previewMode) {
        query = query.eq("status", "published");
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return data as unknown as ContentItemRow | null;
    },
    enabled: !!slug,
    staleTime: previewMode ? 0 : 5 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Fetch all content items of a given type.
 * Only published items shown publicly.
 */
export const useContentItems = (type: string) => {
  return useQuery({
    queryKey: ["content-items", type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_items")
        .select("*")
        .eq("type", type)
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as ContentItemRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
};
